/**
 * FleetHud Page — /fleet route component.
 *
 * CONSUMERS:
 * - frontend/src/App.tsx: Route registration at /fleet
 * - frontend/src/components/fleet/*: Sub-components (NowCard, ActionRequired, RecentRuns, FleetHealth)
 *
 * DEAD-CODE NOTE: This component is actively used via the /fleet route in App.tsx.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppLayout from '../components/AppLayout';
import {
  getSoundEnabled,
  playSoundEvent,
  soundEvents,
  type BridgePollSnapshot,
} from '../components/bridge/bridgeSound';
import {
  BridgeShell,
  BridgeCore,
  RailBlock,
  RailStatus,
  AgentRosterList,
  RailScoreboard,
  useBridgeMode,
  BELT_AGENTS,
  ReplayPlayer,
} from '../components/bridge';
import {
  NowCard,
  ActionRequired,
  RecentRuns,
  FleetHealth,
  AlivenessStrip,
  ReactorCore,
  AssemblyLine,
  STAGE_AGENTS,
  stageFromSessions,
  reactorStageForPhase,
  type ReactorCoreOverview,
  // Mood engine — consumed by this page; re-exported via fleet barrel for
  // dead-code analyzer visibility (see frontend/src/components/fleet/index.ts)
  ACTIVE_STATUSES,
  type FleetMood,
  MOOD_STYLE,
  deriveMood,
} from '../components/fleet';
import { parseTs } from '../components/fleet/fleetTime';
import { usePrefersReducedMotion } from '../components/bridge/BridgeCore';
import type { SelectedInstrument } from '../components/bridge/BridgeDetail';
import {
  FleetEscalation,
  FleetJournalEntry,
  FleetOverview,
  FleetSession,
  fetchEscalations,
  fetchJournalTail,
  fetchOverview,
  fetchRun,
} from '../services/fleetHudApi';

const POLL_MS = 30_000;

// Agent roster for the BridgeCore caption — derived from the canonical
// stage→agent map (AssemblyLine's STAGE_AGENTS) plus the run-level belt
// (agentOrbit's BELT_AGENTS), never a hardcoded count.
const AGENT_ROSTER: readonly string[] = Object.freeze(
  Array.from(new Set([...Object.values(STAGE_AGENTS).flat(), ...BELT_AGENTS]))
);

/**
 * Item 11 spotlight CSS (fh- prefixed). While a BridgeDetail selection is
 * open, every NON-selected instrument — constellation nodes (.ac-node),
 * stage arcs (bc-arc-*), band columns (data-stage-id) — fades to ~40%
 * opacity via a 300ms ease-out transition; the selected instrument carries
 * data-selected="true" and is excluded by the :not() guard.
 *
 * RED LINE (c) — the ACTION REQUIRED label and the approve/accept path are
 * NEVER dimmed: enforced twice, structurally (the fade selectors only match
 * instruments, so rail content is unreachable) AND by the explicit
 * !important guard below covering every action-required/approve-path marker
 * pattern used across the bridge.
 *
 * RED LINE (d) — reduced motion: the CSS media query is the browser half;
 * fh-spotlight--static (matchMedia-driven) is the JS half, making the fade
 * instant where media queries are never evaluated (jsdom/tests).
 */
const FH_CSS = `
.fh-spotlight .ac-node:not([data-selected="true"]),
.fh-spotlight [data-testid^="bc-arc-"]:not([data-selected="true"]),
.fh-spotlight [data-stage-id]:not([data-selected="true"]) {
  opacity: 0.4;
  transition: opacity 300ms ease-out;
}
.fh-spotlight [data-testid^="action-required"],
.fh-spotlight [data-action-required],
.fh-spotlight [data-approve-path] {
  opacity: 1 !important;
  transition: none !important;
}
.fh-spotlight--static .ac-node,
.fh-spotlight--static [data-testid^="bc-arc-"],
.fh-spotlight--static [data-stage-id] {
  transition: none !important;
}
@media (prefers-reduced-motion: reduce) {
  .fh-spotlight .ac-node,
  .fh-spotlight [data-testid^="bc-arc-"],
  .fh-spotlight [data-stage-id] {
    transition: none !important;
  }
}
`;

export default function FleetHud() {
  const [overview, setOverview] = useState<FleetOverview | null>(null);
  const [escalations, setEscalations] = useState<FleetEscalation[]>([]);
  const [mood, setMood] = useState<FleetMood>('idle');
  const [journalTail, setJournalTail] = useState<FleetJournalEntry[]>([]);
  const [polledAt, setPolledAt] = useState<number | null>(null);
  // Active run's sessions — fed to the AssemblyLine. undefined = unavailable
  // (idle, or the run-detail fetch failed): the board degrades, never lies.
  const [runSessions, setRunSessions] = useState<FleetSession[] | undefined>(undefined);

  // Wave-3 item 3: previous poll snapshot for the event-sound diff. A ref,
  // not state — it must never trigger a re-render.
  const prevSnapshotRef = useRef<BridgePollSnapshot | null>(null);

  // Wave-2 bridge: persisted toggle (localStorage read on mount only, written
  // only on explicit toggle — see useBridgeMode). bridgeMode is true only at
  // lg+ with the preference on, so the BridgeShell branch below is reachable
  // on every desktop load — not dead code.
  const { bridgeMode, toggleBridgeMode, isCompact, toggleCompact } = useBridgeMode();

  // Item 11: BridgeCore lifts the BridgeDetail selection and reports it here
  // via onSelectionChange; the page applies the spotlight class at the bridge
  // root so it reaches the core AND the band (both live under this node).
  const [selectedInstrument, setSelectedInstrument] = useState<SelectedInstrument | null>(null);
  const reducedMotion = usePrefersReducedMotion();

  // Wave-3 replay: set ONLY by the REPLAY button on a terminal RecentRuns
  // row (explicit user gesture — no auto-open). Clearing it unmounts the
  // overlay entirely (Esc / banner close both land here).
  const [replayRunId, setReplayRunId] = useState<string | null>(null);
  const replayOverlay = replayRunId ? (
    <ReplayPlayer runId={replayRunId} onClose={() => setReplayRunId(null)} />
  ) : null;

  // The ONLY polled data: overview + open escalations. Run detail, journals,
  // and agent health are all fetch-on-interaction inside their components —
  // EXCEPT during an active run, when the poll also pulls the active run's
  // sessions + 8-line journal tail to feed the mood engine / aliveness strip
  // / assembly line. Idle polls keep the exact pre-cinema shape: overview +
  // escalations only (fetchRun is NOT called — AC5).
  const refresh = useCallback(async () => {
    try {
      const [ov, esc] = await Promise.all([fetchOverview(), fetchEscalations()]);
      const escList = Array.isArray(esc) ? esc : [];
      setOverview(ov);
      setEscalations(escList);
      setPolledAt(Date.now());

      const run = ov?.latest_run ?? null;
      const active = !!run && !!run.status && ACTIVE_STATUSES.has(run.status);
      let sessions: FleetSession[] = [];
      let sessionsAvailable = false;
      if (active && run) {
        // Inner guard: a failed run-detail fetch must not sink the overview
        // snapshot — the AssemblyLine just degrades on undefined sessions.
        try {
          const [sess, journal] = await Promise.all([
            fetchRun(run.run_id),
            fetchJournalTail(run.run_id, 8),
          ]);
          sessions = Array.isArray(sess) ? sess : [];
          sessionsAvailable = true;
          setJournalTail(Array.isArray(journal?.entries) ? journal.entries : []);
          setRunSessions(sessions);
        } catch {
          setRunSessions(undefined);
        }
      } else {
        setJournalTail([]);
        setRunSessions(undefined);
      }
      if (ov) setMood(deriveMood(ov, escList, sessions));

      // Wave-3 item 3: diff this poll against the previous snapshot and play
      // one tone per event — ONLY when the persisted pref is on. Built purely
      // from data this poll already returned (overview items_summary,
      // open-escalation count, the same stage derivation ReactorCore uses);
      // nothing is synthesized. The first poll just seeds the ref.
      const nextSnapshot: BridgePollSnapshot = {
        stage:
          (active && sessionsAvailable ? stageFromSessions(sessions) : null) ??
          reactorStageForPhase(run?.current_phase),
        escalation_count: escList.length,
        items_summary: Object.fromEntries(
          (run?.items_summary ?? [])
            .filter((it) => !!it?.item)
            .map((it) => [
              it.item as string,
              it.commit ? `${it.status ?? ''}|${it.commit}` : (it.status ?? ''),
            ])
        ),
      };
      const prevSnapshot = prevSnapshotRef.current;
      prevSnapshotRef.current = nextSnapshot;
      if (prevSnapshot && getSoundEnabled()) {
        for (const event of soundEvents(prevSnapshot, nextSnapshot)) {
          playSoundEvent(event);
        }
      }
    } catch {
      // api util already surfaced the error toast — keep the last good snapshot.
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = window.setInterval(() => {
      // Hidden tabs don't poll — no network traffic while the page isn't visible.
      if (document.visibilityState !== 'visible') return;
      refresh();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const latestRun = overview?.latest_run ?? null;
  const activeRunId =
    latestRun && latestRun.status && ACTIVE_STATUSES.has(latestRun.status)
      ? latestRun.run_id
      : null;

  // ReactorCore view-model — derived ONLY from fields the overview endpoint
  // already returns (current_phase → stage, items_summary → ticks,
  // created_at → elapsed anchor + last-run timestamp). No new fetches/state.
  const reactorOverview: ReactorCoreOverview = (() => {
    const createdMs = parseTs(latestRun?.created_at);
    return {
      // Wave-3 polish: stage from live SESSIONS (same truth as the assembly
      // board); the phase-number mapping is only the fallback when sessions
      // are unavailable — it reads PLAN through the whole run otherwise.
      stage:
        (runSessions ? stageFromSessions(runSessions) : null) ??
        reactorStageForPhase(latestRun?.current_phase),
      items_summary: latestRun?.items_summary ?? [],
      elapsed_s: createdMs !== null ? Math.max(0, (Date.now() - createdMs) / 1000) : 0,
      last_run_at: latestRun?.created_at ?? null,
    };
  })();

  // Shared by both render paths — identical props, identical DOM.
  const alivenessStrip = (
    <AlivenessStrip
      activeRunId={activeRunId}
      lastJournalLine={journalTail.length > 0 ? journalTail[journalTail.length - 1] : null}
      journalAgeSeconds={overview?.journal_age_seconds ?? null}
      pollCountdownSeconds={POLL_MS / 1000}
      mood={mood}
      journalTail={journalTail}
      polledAt={polledAt}
    />
  );

  // WAVE-2 BRIDGE PATH — reachable at lg+ whenever the persisted bridge
  // preference is on (the default at lg+). Pure re-slotting: every fleet
  // component below is rendered with the exact props the wave-1 path uses;
  // no component internals are touched.
  if (bridgeMode) {
    return (
      <div
        className={`bg-base text-text-primary${selectedInstrument ? ' fh-spotlight' : ''}${
          selectedInstrument && reducedMotion ? ' fh-spotlight--static' : ''
        }`}
        style={{ '--mood-glow': MOOD_STYLE[mood].glow } as React.CSSProperties}
        data-spotlight={selectedInstrument ? 'true' : 'false'}
      >
        <style>{FH_CSS}</style>
        <BridgeShell
          compact={isCompact}
          mood={mood}
          polledAt={polledAt}
          pollMs={POLL_MS}
          isRunActive={activeRunId !== null}
          onToggleCompact={toggleCompact}
          onExitToConsole={toggleBridgeMode}
          leftRail={
            <>
              {/* Wave-2 item 5: STATUS is RailStatus (NowCard's facts,
                  densified). NowCard itself survives untouched in the
                  compact/mobile path below. */}
              <RailBlock title="Status" className="shrink-0">
                <RailStatus mood={mood} overview={overview} polledAt={polledAt} />
              </RailBlock>
              {/* ActionRequired logic bit-for-bit identical — same props as
                  the wave-1 path, never conditionally dimmed or gated.
                  shrink-0: the approve path can NEVER be squeezed/clipped by
                  the roster's flex-grow scroll region. */}
              <RailBlock title="Action Required" className="shrink-0">
                <ActionRequired
                  escalations={escalations}
                  latestRun={overview?.latest_run ?? null}
                  onApproved={refresh}
                  onAcked={refresh}
                />
              </RailBlock>
              {/* Agent roster — the rail's scrolling flex-grow region. */}
              <RailBlock
                title={`Agents · ${AGENT_ROSTER.length}`}
                className="min-h-0 flex-1"
              >
                <AgentRosterList
                  sessions={runSessions}
                  activeRunId={activeRunId}
                  mood={mood}
                />
              </RailBlock>
            </>
          }
          center={
            <>
              <div className="flex flex-1 items-center justify-center">
                {isCompact ? (
                  /* COMPACT sub-mode keeps the wave-1 core — ReactorCore is
                      the compact/phone variant and is never dropped. */
                  <ReactorCore mood={mood} overview={reactorOverview} />
                ) : (
                  <BridgeCore
                    mood={mood}
                    overview={reactorOverview}
                    runId={latestRun?.run_id ?? null}
                    alive={overview?.orchestrator_alive !== false}
                    roster={AGENT_ROSTER}
                    pollMs={POLL_MS}
                    polledAt={polledAt}
                    sessions={runSessions}
                    onSelectionChange={setSelectedInstrument}
                  />
                )}
              </div>
              <AssemblyLine
                variant="band"
                activeRunId={activeRunId}
                sessions={runSessions}
                items={latestRun?.items_summary}
                journalAgeSeconds={overview?.journal_age_seconds ?? null}
                selectedStage={
                  selectedInstrument?.kind === 'stage' ? selectedInstrument.id : null
                }
              />
            </>
          }
          rightRail={
            <>
              {/* Item 6: dense, data-honest right rail. RailScoreboard is fed
                  ONLY from already-polled state — the overview's latest_run
                  projection wrapped as a one-run list (FleetOverviewRun is a
                  structural subset of FleetRun) plus the polled escalations.
                  ZERO new fetch calls: RecentRuns keeps owning its own runs
                  fetch (AC: its data behaviour is unchanged), and the
                  scoreboard never fabricates a window it wasn't given. Every
                  child passes hideHeader so the RailBlock title is the sole
                  header — no double-printed panel titles. */}
              <RailBlock title="RECENT">
                <RailScoreboard
                  runs={latestRun ? [latestRun] : []}
                  escalations={escalations}
                  journalAgeSeconds={overview?.journal_age_seconds ?? null}
                  hideHeader
                />
              </RailBlock>
              <RailBlock title="Recent Runs">
                <RecentRuns dense hideHeader onReplayRun={setReplayRunId} />
              </RailBlock>
              <RailBlock title="Fleet Health">
                <FleetHealth dense hideHeader />
              </RailBlock>
            </>
          }
          bottomStrip={
            <AlivenessStrip
              variant="bar"
              activeRunId={activeRunId}
              lastJournalLine={journalTail.length > 0 ? journalTail[journalTail.length - 1] : null}
              journalAgeSeconds={overview?.journal_age_seconds ?? null}
              pollCountdownSeconds={POLL_MS / 1000}
              mood={mood}
              journalTail={journalTail}
              polledAt={polledAt}
            />
          }
        />
        {replayOverlay}
      </div>
    );
  }

  // WAVE-1 STACKED PATH — below lg, or bridge preference off. Unchanged.
  return (
    <AppLayout>
      {/* --mood-glow is inline on the page root (not document.documentElement)
          so it cannot leak across route navigations; it only changes when the
          derived mood changes, not on every 1s tick. */}
      <div
        className="min-h-screen bg-base text-text-primary"
        style={{ '--mood-glow': MOOD_STYLE[mood].glow } as React.CSSProperties}
      >
        <div className="mx-auto max-w-5xl px-4 py-6 space-y-4">
          <header className="flex items-baseline justify-between">
            <h1 className="font-heading text-2xl text-text-primary">Fleet HUD</h1>
            <div className="flex items-baseline gap-3">
              {/* Re-entry to the bridge — ⌂ CONSOLE persists the exit, and without
                  this button the console was a DEAD END (owner got stuck,
                  2026-08-14). lg-only: useBridgeMode never renders the bridge
                  below lg, so the button would silently no-op there. */}
              <button
                type="button"
                onClick={toggleBridgeMode}
                data-testid="enter-bridge"
                className="hidden lg:inline-block rounded-sm border border-brand px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.15em] text-brand hover:bg-brand hover:text-black"
              >
                Bridge
              </button>
              <span className="font-mono text-xs text-text-muted">polls 30s · visible tab only</span>
            </div>
          </header>

          {alivenessStrip}
          <NowCard overview={overview} escalations={escalations} />
          <ReactorCore mood={mood} overview={reactorOverview} />
          <AssemblyLine
            activeRunId={activeRunId}
            sessions={runSessions}
            items={latestRun?.items_summary}
            journalAgeSeconds={overview?.journal_age_seconds ?? null}
          />
          <ActionRequired
            escalations={escalations}
            latestRun={overview?.latest_run ?? null}
            onApproved={refresh}
            onAcked={refresh}
          />
          <RecentRuns onReplayRun={setReplayRunId} />
          <FleetHealth />
        </div>
        {replayOverlay}
      </div>
    </AppLayout>
  );
}
